import {
  Avatar,
  AvatarGroup,
  Box,
  Card,
  CardActionArea,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded'
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded'
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded'
import { useNavigate } from 'react-router-dom'
import type { Session, Speaker } from '../data/types'
import { formatTimeRange } from '../utils/format'
import SessionTypeChip from './ui/SessionTypeChip'
import { useOptionalAppData } from '../contexts/AppDataContext'
import { useAppBase, appPath } from './appBase'
import { resolveAssetUrl } from '../services/assetUrl'

interface SessionCardProps {
  session: Session
  speakers: Speaker[]
}

const NON_SAVEABLE: Session['type'][] = ['break']

export default function SessionCard({ session, speakers }: SessionCardProps) {
  const navigate = useNavigate()
  const base = useAppBase()
  // Optional: absent in the admin page-builder preview (no AppDataProvider).
  // When absent, the attendee-only "save to my schedule" control is hidden.
  const appData = useOptionalAppData()
  const sessionSpeakers = speakers.filter((s) => session.speakerIds.includes(s.id))
  const saved = appData?.isSaved(session.id) ?? false
  const canSave = appData !== null && !NON_SAVEABLE.includes(session.type)

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        transition: 'border-color .15s, box-shadow .15s',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: '0 6px 20px rgba(28,85,128,0.08)',
        },
      }}
    >
      <CardActionArea
        onClick={() => navigate(appPath(base, `agenda/${session.id}`))}
        sx={{ p: 2, alignItems: 'stretch' }}
      >
        <Stack direction="row" spacing={2}>
          {/* Time column */}
          <Box
            sx={{
              minWidth: 68,
              pt: 0.5,
              borderRight: '1px solid',
              borderColor: 'divider',
              pr: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
              {formatTimeRange(session.startsAt, session.endsAt).split(' – ')[0]}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTimeRange(session.startsAt, session.endsAt).split(' – ')[1]}
            </Typography>
          </Box>

          {/* Body */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.5, flexWrap: 'wrap' }}
            >
              <SessionTypeChip type={session.type} />
              {session.track && (
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {session.track}
                </Typography>
              )}
            </Stack>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              {session.title}
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ mt: 0.5, color: 'text.secondary' }}
            >
              <PlaceRoundedIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" color="text.secondary">
                {session.location}
              </Typography>
            </Stack>

            {sessionSpeakers.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <AvatarGroup
                  max={4}
                  sx={{
                    '& .MuiAvatar-root': {
                      width: 26,
                      height: 26,
                      fontSize: '0.7rem',
                      borderColor: 'background.paper',
                    },
                  }}
                >
                  {sessionSpeakers.map((sp) => (
                    <Avatar key={sp.id} src={resolveAssetUrl(sp.photoUrl)} sx={{ bgcolor: sp.avatarColor }}>
                      {sp.initials}
                    </Avatar>
                  ))}
                </AvatarGroup>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {sessionSpeakers.map((s) => s.name).join(', ')}
                </Typography>
              </Stack>
            )}
          </Box>

          {/* Save toggle */}
          {canSave && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Tooltip title={saved ? 'Remove from my schedule' : 'Add to my schedule'}>
                <IconButton
                  size="small"
                  color={saved ? 'primary' : 'default'}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    appData?.toggleSavedSession(session.id)
                  }}
                >
                  {saved ? <BookmarkRoundedIcon /> : <BookmarkBorderRoundedIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Stack>
      </CardActionArea>
    </Card>
  )
}
